/**
 * Publish / read a page's human-readable "page state" for the Support >
 * Report an issue dialog.
 *
 * ISD-357 (Steven, 2026-08): the ticket's Page state field showed
 * `item_types: I`, the raw URL parameter, when he had actually picked
 * "Merchandise only" in the Item Sales filter bar. A support agent reading
 * the ticket has no way to know `I` means merchandise-only without opening
 * the code. This module lets a page publish the SAME labels it already
 * shows on screen (e.g. "Items included: Merchandise only"), so a ticket
 * reads in the operator's words instead of the URL's.
 *
 * A page that calls `useReportPageState` gets its entries sent as
 * `page_state` on the next report. A page that never calls it leaves the
 * report dialog on the existing behavior: the raw `url` (including its
 * query string), which the api parses itself.
 *
 * Deliberately a plain module-level slot, not React context. The report
 * dialog is mounted in the app shell (`Layout.tsx`), which sits outside the
 * routed page tree, so there is no shared provider boundary to hang a
 * context on. A page writes into this slot while mounted; the shell reads
 * it once, at the moment the operator clicks "Report an issue".
 */

import { useEffect } from 'react';

export type ReportPageStateEntry = { label: string; value: string };

let currentEntries: ReportPageStateEntry[] = [];

/** The active page's published entries, or `[]` if no page has published any. */
export function getReportPageState(): ReportPageStateEntry[] {
  return currentEntries;
}

/**
 * Publishes `entries` as the current page state for as long as the calling
 * component stays mounted with this value. Cleared when the component
 * unmounts (which covers a route change, since the outgoing page unmounts as
 * part of it) or when `entries` changes to a new array before that. The
 * identity check in the cleanup stops a stale effect from clobbering a
 * different page's entries that has already taken the slot.
 */
export function useReportPageState(entries: ReportPageStateEntry[]): void {
  useEffect(() => {
    currentEntries = entries;
    return () => {
      if (currentEntries === entries) currentEntries = [];
    };
  }, [entries]);
}
